import { useState } from "react";
import { CreateEntityButton } from "./CreateEntityButton";
import { DialogActions, Modal } from "./Modal";
import { Field, Select, TextArea, TextInput } from "./Field";
import type { Account, CreateMilestoneInput } from "../data/types";

/**
 * "New milestone" — button, dialog and confirmation in one, mirroring
 * AddAccountButton. Persistence is the caller's job via onCreated (a POST +
 * revalidate), so the dialog stays pure and testable.
 */
export function NewMilestoneButton({ accounts, onCreated }: { accounts: Account[]; onCreated?: (input: CreateMilestoneInput) => void }) {
  return (
    <CreateEntityButton<CreateMilestoneInput>
      label="New milestone"
      buildConfirmation={(input) => `${input.title} added`}
      onResult={onCreated}
    >
      {(close, create) => <NewMilestoneDialog accounts={accounts} onClose={close} onCreate={create} />}
    </CreateEntityButton>
  );
}

function NewMilestoneDialog({ accounts, onClose, onCreate }: { accounts: Account[]; onClose: () => void; onCreate: (i: CreateMilestoneInput) => void }) {
  const [title, setTitle] = useState("");
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  const [eventDate, setEventDate] = useState("");
  const [detail, setDetail] = useState("");
  const [portalVisible, setPortalVisible] = useState(false);
  const [touched, setTouched] = useState(false);

  const titleMissing = title.trim() === "";
  const dateMissing = eventDate === "";

  function submit() {
    if (titleMissing || dateMissing || !accountId) {
      setTouched(true);
      return;
    }
    onCreate({ accountId, title: title.trim(), eventDate, detail: detail.trim(), portalVisible });
  }

  return (
    <Modal open onClose={onClose} title="New milestone" footer={<DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Add milestone" />}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="What is happening" htmlFor="nm-title" span2>
          <TextInput
            id="nm-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && titleMissing}
            placeholder="Certified tier launch"
          />
          {touched && titleMissing && <span className="text-xs text-error-600">A title is required.</span>}
          <span className="text-xs text-[var(--text-3)]">The customer sees this title if you make the milestone visible to them.</span>
        </Field>

        <Field label="Account" htmlFor="nm-account">
          <Select id="nm-account" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Date" htmlFor="nm-date">
          <TextInput id="nm-date" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} aria-invalid={touched && dateMissing} />
          {touched && dateMissing && <span className="text-xs text-error-600">A date is required.</span>}
        </Field>

        <Field label="Notes" htmlFor="nm-detail" hint="(internal)" span2>
          <TextArea id="nm-detail" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="What has to be true on the day, and who is counting on it" />
        </Field>

        <Field label="Customer portal" htmlFor="nm-portal" span2>
          <label className="flex items-center gap-2 text-sm">
            <input id="nm-portal" type="checkbox" checked={portalVisible} onChange={(e) => setPortalVisible(e.target.checked)} />
            Show this date to the customer
          </label>
        </Field>
      </div>
    </Modal>
  );
}
